/**
 * <RoutePicker> — searchable combo-box for picking an in-app route from a
 * curated tree of modules → screens → subtabs.
 *
 * Falls back gracefully to a free-form input for advanced users who want to
 * paste a custom path (e.g. deep-linked entity URLs).
 */
import { useMemo, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList, CommandGroup, CommandEmpty } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { ChevronsUpDown, Check, ExternalLink } from "lucide-react";
import { APP_ROUTES, flattenAppRoutes } from "@/lib/appRoutes";

export default function RoutePicker({ value = "", onChange, testid = "route-picker" }) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const flat = useMemo(flattenAppRoutes, []);
  const match = flat.find((r) => r.path === value);
  const label = match ? `${match.group} · ${match.label}` : value || "";

  if (customMode) {
    return (
      <div className="flex gap-2" data-testid={`${testid}-custom`}>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/app/… custom path"
          className="h-8 rounded-none text-xs flex-1"
        />
        <button
          type="button"
          onClick={() => setCustomMode(false)}
          className="text-[10px] text-primary hover:underline"
        >
          Pick from list
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 w-full" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex-1 min-w-0 h-8 rounded-none border border-border bg-background px-2 flex items-center gap-2 text-xs hover:border-primary/50 transition"
            data-testid={`${testid}-trigger`}
          >
            <span className="flex-1 truncate text-left">
              {label || <span className="text-muted-foreground">Pick an in-app destination…</span>}
            </span>
            <ChevronsUpDown className="w-3 h-3 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-96" align="start">
          <Command>
            <CommandInput placeholder="Search modules & screens…" className="h-9 text-xs" />
            <CommandList className="max-h-72">
              <CommandEmpty>No matching destinations.</CommandEmpty>
              {APP_ROUTES.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {(group.children || []).map((leaf) => (
                    <CommandItem
                      key={leaf.path + leaf.label}
                      value={`${group.label} ${leaf.label} ${leaf.path}`}
                      onSelect={() => { onChange(leaf.path); setOpen(false); }}
                      className="text-xs"
                      data-testid={`${testid}-item-${leaf.path}`}
                    >
                      <Check className={`w-3 h-3 mr-1.5 ${value === leaf.path ? "opacity-100" : "opacity-0"}`} />
                      <span className="flex-1 truncate">{leaf.label}</span>
                      <span className="text-[10px] font-mono text-muted-foreground ml-2 truncate">{leaf.path}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        onClick={() => setCustomMode(true)}
        className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1 shrink-0"
        title="Enter a custom path"
      >
        <ExternalLink className="w-3 h-3" /> custom
      </button>
    </div>
  );
}
